import { Button, SegmentedControl } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useFetchExpenseStats from 'src/Hooks/Queries/useFetchExpenseStats';
import { useCanCreate } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { CategoryStatsDto } from 'src/Services/API/generated';
import { getCategoryIcon } from 'src/Utils/categoryIcons';

import styles from './CategoryBreakdown.module.scss';

interface Props {
  year: number;
}

type Mode = 'expense' | 'income';

const formatEur = (amount: number): string =>
  amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 });

const sortDesc = (rows: CategoryStatsDto[] | undefined): CategoryStatsDto[] =>
  [...(rows ?? [])].sort((a, b) => b.total - a.total);

const CategoryBreakdown = ({ year }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useFetchExpenseStats(year.toString());
  const { hasPermission: canCreate } = useCanCreate('expense');
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const [mode, setMode] = useState<Mode>('expense');

  const expenseRows = useMemo(() => sortDesc(data?.expensesByCategory), [data?.expensesByCategory]);
  const incomeRows = useMemo(() => sortDesc(data?.revenuesByCategory), [data?.revenuesByCategory]);

  const rows = mode === 'expense' ? expenseRows : incomeRows;
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const max = rows[0]?.total ?? 0;

  const handleAddExpense = () => navigate('new', { state: { isIncome: false } });

  const expenseTotal = expenseRows.reduce((sum, row) => sum + row.total, 0);
  const incomeTotal = incomeRows.reduce((sum, row) => sum + row.total, 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('expenses.breakdown.title', "Où va l'argent en {{year}} ?", { year })}
        </h3>
        <SegmentedControl
          value={mode}
          onChange={value => setMode(value as Mode)}
          data={[
            {
              value: 'expense',
              label: `${t('expenses.stats.expenseLabel', 'Dépenses')} · ${formatEur(expenseTotal)} €`,
            },
            {
              value: 'income',
              label: `${t('expenses.stats.incomeLabel', 'Recettes')} · ${formatEur(incomeTotal)} €`,
            },
          ]}
        />
      </div>

      {rows.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyText}>
            {t('expenses.breakdown.empty', 'Aucune opération pour cette année')}
          </p>
          {canCreate && (
            <Button
              onClick={handleAddExpense}
              leftSection={<IconPlus size={18} />}
              disabled={assetAboveQuotas}
            >
              {t('expenses.actions.addExpense', 'Ajouter une dépense')}
            </Button>
          )}
        </div>
      ) : (
        <ul className={styles.list}>
          {rows.map(row => {
            const Icon = getCategoryIcon(row.category, mode === 'income');
            const barWidth = max > 0 ? (row.total / max) * 100 : 0;
            const percent = total > 0 ? (row.total / total) * 100 : 0;

            return (
              <li key={row.category} className={styles.row}>
                <div className={styles.rowHeader}>
                  <div className={styles.rowLabel}>
                    <Icon size={18} className={styles.rowIcon} />
                    <span>{t(`expense.mainCategory.${row.category}`)}</span>
                  </div>
                  <div className={styles.rowValues}>
                    <span className={styles.rowAmount}>{formatEur(row.total)} €</span>
                    <span className={styles.rowPercent}>{percent.toFixed(0)}%</span>
                  </div>
                </div>
                <div className={styles.barTrack}>
                  <div
                    className={mode === 'expense' ? styles.barFillExpense : styles.barFillIncome}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CategoryBreakdown;
